// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import dayjs from 'dayjs';

import { css } from '@emotion/css';
import { Theme, useTheme } from '@mui/material';

import { useKeyPress } from '../hooks/useKeyPress';

import { getCurrentPosition, getCurrentTime, getLastScrollPosition, setLastScrollPosition } from '@components/Timeline/Timeline/utils';

import { useTimeline } from '@components/Timeline/Timeline/TimelineProvider';
import TimelineIndex from '@components/Timeline/Timeline/TimelineIndex';
import TimelineArea from '@components/Timeline/Timeline/TimelineArea';

// COMPONENT ---------------------------------------------------------------- //

const TimelineContent = () => {
  const theme = useTheme();
  const cls = useStyles(theme);

  const {
    currentPosition,
    date,
    intervalCallback,
    isCurrentDay,
    markerUpdateMs,
    onTimelineReady,
    scrollKey,
    setCurrentPosition,
    setCurrentTime,
    setIsCurrentDay,
    timelineRef,
    timezoneOverride,
    zoom,
  } = useTimeline();

  // KEYPRESSES //
  const goToNowKeypress = useKeyPress('n');

  /** Handle the timeline scroll */
  const handleTimelineScroll = () => {
    const scrollValue = timelineRef?.current?.scrollLeft;
    if (scrollValue) setLastScrollPosition(scrollKey, scrollValue, zoom);
  };

  /** Scroll to the current position or the last scroll position */
  const scrollToDefaultPosition = () => {
    if (timelineRef?.current) {
      if (isCurrentDay) {
        timelineRef.current.scrollLeft = currentPosition - 240;
      } else {
        const position = getLastScrollPosition(scrollKey, zoom);
        timelineRef.current.scrollLeft = position;
      }
    }
  };

  /** Update the current position and time with a callback */
  const updateInterval = () => {
    setCurrentPosition(getCurrentPosition(date, zoom, timezoneOverride));
    setCurrentTime(getCurrentTime(timezoneOverride));
    if (intervalCallback) intervalCallback();
  };

  // On mount, scroll to default position and expose scroll function
  React.useEffect(() => {
    scrollToDefaultPosition();

    // Expose the scroll function to parent components
    if (onTimelineReady && timelineRef?.current) {
      const setScrollPosition = (scrollPosition: number) => {
        if (timelineRef?.current) {
          timelineRef.current.scrollLeft = scrollPosition;
        }
      };
      onTimelineReady(setScrollPosition);
    }
  }, []);

  // On keypress or timeline date change, scroll to default position
  React.useEffect(() => {
    scrollToDefaultPosition();
  }, [goToNowKeypress, date]);

  // Check if the timeline date is today when it changes
  React.useEffect(() => {
    setIsCurrentDay(dayjs(date).isSame(dayjs(), `day`));
  }, [date]);

  // Timer interval to update the marker position
  // Reset upon changing base timeline properties
  const timer = React.useRef<NodeJS.Timeout | null>(null);
  React.useEffect(() => {
    // Call the initial update on change
    // Then, clear the previous interval (if it exists)
    // Then, set the new interval (if applicable)
    updateInterval();
    if (timer?.current) clearInterval(timer.current);
    if (isCurrentDay && markerUpdateMs > 0) timer.current = setInterval(() => updateInterval(), markerUpdateMs);

    // Clear the interval on unmount
    return () => clearInterval(timer.current);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCurrentDay, markerUpdateMs, date, zoom]);

  return (
    <div ref={timelineRef} className={cls.timelineWrapper} onScroll={handleTimelineScroll}>
      <div className={cls.timeline}>
        <TimelineIndex />
        <TimelineArea />
      </div>
    </div>
  );
};

// STYLES --------------------------------------------------------------------- //

const useStyles = (theme: Theme) => ({
  timelineWrapper: css`
    position: relative;
    overflow: auto;
    flex: 1;
    width: 100%;
    height: 100%;
    background-color: ${theme.palette.background.paper};
  `,
  timeline: css`
    position: relative;
    display: flex;
    flex: 1;
    width: max-content;
    height: max-content;
  `,
});

// -------------------------------- EXPORT -------------------------------- //

export default TimelineContent;
